import { useState, useEffect, useRef } from "react";
import { notifyNewPoster } from "../../utils/notifications";
import { collection, addDoc, getDocs, deleteDoc, doc, serverTimestamp, orderBy, query } from "firebase/firestore";
import { db } from "../../firebase";
import toast, { Toaster } from "react-hot-toast";
import { Upload, Trash2, ImagePlus, Eye, Calendar, X } from "lucide-react";

const CLOUD_NAME    = "dan0bapfg";
const UPLOAD_PRESET = "htst_event";

const TYPE_COLORS = {
  event:        { bg:"rgba(59,130,246,0.15)",  color:"#3b82f6", label:"Event" },
  announcement: { bg:"rgba(245,158,11,0.15)",  color:"#f59e0b", label:"Announcement" },
  drive:        { bg:"rgba(34,197,94,0.15)",   color:"#22c55e", label:"Drive" },
  camp:         { bg:"rgba(168,85,247,0.15)",  color:"#a855f7", label:"Camp" },
  urgent:       { bg:"rgba(239,68,68,0.15)",   color:"#ef4444", label:"Urgent" },
};

export default function EventPosters() {
  const [posters,   setPosters]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress,  setProgress]  = useState(0);
  const [preview,   setPreview]   = useState(null);
  const [selected,  setSelected]  = useState(null);
  const [form,      setForm]      = useState({ title:"", date:"", description:"", type:"event" });
  const fileRef = useRef(null);

  useEffect(() => { loadPosters(); }, []);

  async function loadPosters() {
    try {
      const snap = await getDocs(query(collection(db, "announcements"), orderBy("createdAt", "desc")));
      setPosters(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }

  function handleFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please select an image file");
    if (file.size > 10 * 1024 * 1024) return toast.error("Image must be under 10MB");
    setPreview({ file, url: URL.createObjectURL(file) });
  }

  async function handleUpload() {
    if (!preview?.file) return toast.error("Please select an image first");
    if (!form.title)    return toast.error("Please enter a title");

    setUploading(true);
    setProgress(10);

    try {
      // Upload to Cloudinary
      const formData = new FormData();
      formData.append("file",         preview.file);
      formData.append("upload_preset", UPLOAD_PRESET);
      formData.append("folder",        "htst_events");

      setProgress(30);

      const res = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`,
        { method:"POST", body: formData }
      );

      setProgress(80);

      if (!res.ok) throw new Error("Cloudinary upload failed");
      const data = await res.json();

      setProgress(90);

      // Save to Firestore
      await addDoc(collection(db, "announcements"), {
        title:       form.title,
        date:        form.date,
        description: form.description,
        type:        form.type,
        imageUrl:    data.secure_url,
        publicId:    data.public_id,
        active:      true,
        createdAt:   serverTimestamp(),
      });
      await notifyNewPoster(form.title);

      setProgress(100);
      toast.success("Poster uploaded & published on home page! 🎉");

      // Reset
      setPreview(null);
      setProgress(0);
      setForm({ title:"", date:"", description:"", type:"event" });
      if (fileRef.current) fileRef.current.value = "";
      loadPosters();

    } catch(e) {
      console.error(e);
      toast.error("Upload failed: " + e.message);
      setProgress(0);
    } finally {
      setUploading(false);
    }
  }

  async function handleDelete(poster) {
    const confirmed = window.confirm(
      `Delete "${poster.title}"?\nIt will be removed from the home page immediately.`
    );
    if (!confirmed) return;
    try {
      await deleteDoc(doc(db, "announcements", poster.id));
      setPosters(p => p.filter(x => x.id !== poster.id));
      toast.success("Poster deleted!");
    } catch(e) {
      console.error(e);
      toast.error("Failed to delete");
    }
  }

  return (
    <div>
      <Toaster position="top-center"/>

      {/* Header */}
      <div style={s.pageHeader}>
        <ImagePlus size={22} color="#f59e0b"/>
        <div>
          <h2 style={s.title}>Event Posters</h2>
          <p style={s.sub}>Upload event posters — they appear on the NGO home page instantly</p>
        </div>
      </div>

      <div style={s.grid2}>
        {/* Upload Form */}
        <div style={s.card}>
          <div style={s.cardTitle}><Upload size={15} color="#f59e0b"/> Upload New Poster</div>

          {/* Drop Zone */}
          <div
            onClick={() => !uploading && fileRef.current?.click()}
            style={{
              ...s.dropZone,
              borderColor: preview ? "#f59e0b" : "rgba(255,255,255,0.1)",
              background:  preview ? "rgba(245,158,11,0.04)" : "rgba(255,255,255,0.02)",
              cursor: uploading ? "not-allowed" : "pointer",
            }}>
            {preview ? (
              <div style={{ position:"relative" }}>
                <img src={preview.url} alt="Preview"
                  style={{ width:"100%", maxHeight:200, objectFit:"cover", borderRadius:8 }}/>
                <button
                  onClick={e => { e.stopPropagation(); setPreview(null); if(fileRef.current) fileRef.current.value=""; }}
                  style={s.removeBtn}>
                  <X size={14}/>
                </button>
              </div>
            ) : (
              <div style={{ textAlign:"center", padding:"32px 20px" }}>
                <ImagePlus size={36} color="#52504d" style={{ marginBottom:10 }}/>
                <div style={{ fontSize:14, fontWeight:600, color:"#78716c", marginBottom:4 }}>
                  Click to select image
                </div>
                <div style={{ fontSize:12, color:"#3a3835" }}>JPG, PNG, WEBP · Max 10MB</div>
              </div>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/*" onChange={handleFileChange} style={{ display:"none" }}/>

          {/* Fields */}
          <div style={{ display:"flex", flexDirection:"column", gap:12, marginTop:14 }}>
            <Field label="Poster Title *"    value={form.title}        onChange={v=>setForm(p=>({...p,title:v}))}       placeholder="Food Drive – May 2026"/>
            <Field label="Event Date"        value={form.date}         onChange={v=>setForm(p=>({...p,date:v}))}        placeholder="May 20, 2026" type="date"/>
            <Field label="Short Description" value={form.description}  onChange={v=>setForm(p=>({...p,description:v}))} placeholder="Brief description..."/>
            <div>
              <label style={s.label}>Type</label>
              <select value={form.type} onChange={e=>setForm(p=>({...p,type:e.target.value}))} style={s.select}>
                <option value="event">Event</option>
                <option value="announcement">Announcement</option>
                <option value="drive">Community Drive</option>
                <option value="camp">Camp</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>

          {/* Progress Bar */}
          {uploading && (
            <div style={{ marginTop:14 }}>
              <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, color:"#78716c", marginBottom:6 }}>
                <span>Uploading to Cloudinary...</span>
                <span>{progress}%</span>
              </div>
              <div style={{ height:6, background:"rgba(255,255,255,0.06)", borderRadius:3, overflow:"hidden" }}>
                <div style={{ height:"100%", width:`${progress}%`, background:"#f59e0b", borderRadius:3, transition:"width 0.4s" }}/>
              </div>
            </div>
          )}

          <button onClick={handleUpload} disabled={uploading} style={{ ...s.uploadBtn, opacity:uploading?0.6:1, marginTop:16 }}>
            <Upload size={16}/>
            {uploading ? `Uploading ${progress}%...` : "Upload & Publish to Home Page"}
          </button>
        </div>

        {/* Live Posters */}
        <div style={s.card}>
          <div style={s.cardTitle}>
            <Eye size={15} color="#f59e0b"/> Live on Home Page
            <span style={s.badge}>{posters.length} posters</span>
          </div>

          {loading ? (
            <div style={s.empty}>Loading...</div>
          ) : posters.length === 0 ? (
            <div style={{ textAlign:"center", padding:"40px 0" }}>
              <ImagePlus size={40} color="#1a1714" style={{ marginBottom:12 }}/>
              <div style={{ fontSize:13, color:"#52504d" }}>No posters yet</div>
              <div style={{ fontSize:12, color:"#3a3835", marginTop:4 }}>Upload one and it shows on the home page</div>
            </div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:10, maxHeight:520, overflowY:"auto" }}>
              {posters.map(poster => {
                const tc = TYPE_COLORS[poster.type] || TYPE_COLORS.event;
                return (
                  <div key={poster.id} style={s.posterRow}>
                    <img src={poster.imageUrl} alt={poster.title}
                      onClick={() => setSelected(poster)}
                      style={{ width:64, height:64, objectFit:"cover", borderRadius:9, flexShrink:0, cursor:"pointer" }}/>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:13, fontWeight:700, color:"#fef3e2", marginBottom:5, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                        {poster.title}
                      </div>
                      <div style={{ display:"flex", gap:6, flexWrap:"wrap", alignItems:"center" }}>
                        <span style={{ fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:50, background:tc.bg, color:tc.color, textTransform:"capitalize" }}>
                          {tc.label}
                        </span>
                        {poster.date && (
                          <span style={{ fontSize:11, color:"#52504d", display:"flex", alignItems:"center", gap:3 }}>
                            <Calendar size={10}/> {poster.date}
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{ display:"flex", gap:6, flexShrink:0 }}>
                      <button onClick={() => setSelected(poster)} style={s.viewBtn}><Eye size={13}/></button>
                      <button onClick={() => handleDelete(poster)} style={s.deleteBtn}><Trash2 size={13}/></button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Full Preview Modal */}
      {selected && (
        <div style={s.overlay} onClick={() => setSelected(null)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <button onClick={() => setSelected(null)} style={s.closeBtn}><X size={18}/></button>
            <img src={selected.imageUrl} alt={selected.title}
              style={{ width:"100%", maxHeight:"65vh", objectFit:"contain", borderRadius:10, marginBottom:16 }}/>
            <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:8 }}>
              <span style={{ background:(TYPE_COLORS[selected.type]||TYPE_COLORS.event).color, color:"#fff", fontSize:10, fontWeight:800, padding:"3px 10px", borderRadius:50, textTransform:"uppercase" }}>
                {(TYPE_COLORS[selected.type]||TYPE_COLORS.event).label}
              </span>
              {selected.date && (
                <span style={{ fontSize:12, color:"#78716c", display:"flex", alignItems:"center", gap:4 }}>
                  <Calendar size={12}/> {selected.date}
                </span>
              )}
            </div>
            <h3 style={{ fontSize:20, fontWeight:800, color:"#fef3e2", margin:"0 0 8px", fontFamily:"'Playfair Display',serif" }}>
              {selected.title}
            </h3>
            {selected.description && (
              <p style={{ fontSize:14, color:"#a8a29e", lineHeight:1.7, margin:0 }}>{selected.description}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type="text" }) {
  return (
    <div>
      <label style={{ display:"block", fontSize:11, fontWeight:700, color:"#78716c", textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:5 }}>{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={{ width:"100%", padding:"10px 12px", background:"#0a0908", border:"1px solid rgba(255,255,255,0.08)", borderRadius:9, color:"#fef3e2", fontSize:13, fontFamily:"inherit", outline:"none", boxSizing:"border-box" }}/>
    </div>
  );
}

const s = {
  pageHeader: { display:"flex", alignItems:"center", gap:14, marginBottom:20, background:"#111009", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:18 },
  title:      { fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:800, color:"#fef3e2", margin:"0 0 4px" },
  sub:        { fontSize:13, color:"#78716c", margin:0 },
  grid2:      { display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, alignItems:"start" },
  card:       { background:"#111009", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:20 },
  cardTitle:  { fontSize:13, fontWeight:700, color:"#fef3e2", marginBottom:16, display:"flex", alignItems:"center", gap:7 },
  badge:      { marginLeft:"auto", fontSize:10, background:"rgba(245,158,11,0.15)", color:"#f59e0b", border:"1px solid rgba(245,158,11,0.25)", padding:"2px 8px", borderRadius:50 },
  dropZone:   { border:"2px dashed", borderRadius:12, overflow:"hidden", transition:"all 0.2s", minHeight:120 },
  removeBtn:  { position:"absolute", top:8, right:8, background:"rgba(0,0,0,0.7)", border:"none", borderRadius:"50%", width:28, height:28, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", color:"#fff" },
  label:      { display:"block", fontSize:11, fontWeight:700, color:"#78716c", textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:5 },
  select:     { width:"100%", padding:"10px 12px", background:"#0a0908", border:"1px solid rgba(255,255,255,0.08)", borderRadius:9, color:"#fef3e2", fontSize:13, fontFamily:"inherit", outline:"none", cursor:"pointer" },
  uploadBtn:  { width:"100%", display:"flex", alignItems:"center", justifyContent:"center", gap:8, padding:"13px", background:"#f59e0b", color:"#0a0908", border:"none", borderRadius:11, fontSize:14, fontWeight:800, cursor:"pointer", fontFamily:"inherit" },
  posterRow:  { display:"flex", alignItems:"center", gap:12, padding:10, background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.05)", borderRadius:10 },
  viewBtn:    { width:30, height:30, borderRadius:7, background:"rgba(59,130,246,0.12)", border:"1px solid rgba(59,130,246,0.2)", color:"#3b82f6", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" },
  deleteBtn:  { width:30, height:30, borderRadius:7, background:"rgba(239,68,68,0.12)", border:"1px solid rgba(239,68,68,0.2)", color:"#ef4444", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" },
  overlay:    { position:"fixed", inset:0, background:"rgba(0,0,0,0.92)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center", padding:20, backdropFilter:"blur(12px)" },
  modal:      { background:"#111009", border:"1px solid rgba(255,255,255,0.1)", borderRadius:20, padding:24, width:"100%", maxWidth:580, position:"relative", maxHeight:"90vh", overflowY:"auto" },
  closeBtn:   { position:"absolute", top:14, right:14, background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:"50%", width:34, height:34, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", color:"#a8a29e", zIndex:1 },
  empty:      { fontSize:13, color:"#52504d", textAlign:"center", padding:"24px 0" },
};
